import { useEffect, useMemo, useState } from "react";
import { useApp, type Match, type MyPick } from "../state";
import { FootballIcon } from "../components/FootballIcon";
import { PageHeader } from "../components/PageHeader";
import { PredictionPills } from "../components/PredictionPills";
import { TeamBadge } from "../components/TeamBadge";
import { submitPrediction } from "../api";
import { countdown, formatLocalTime, formatPtDate, isPtToday, isPtTomorrow } from "../util";

export function Matches() {
  const { state, myPicks, refresh, viewerRole } = useApp();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);

  const groups = useMemo(() => {
    const sorted = [...state.matches].sort((a, b) => a.kickoff_utc.localeCompare(b.kickoff_utc));
    const today: Match[] = [], tomorrow: Match[] = [], later: Match[] = [];
    for (const m of sorted) {
      if (m.status === "final" || m.status === "final_pens") continue;
      // Bucket by PT calendar day so all viewers see the same "today / tomorrow"
      // regardless of their own timezone. Spec anchors the tournament to PT.
      if (isPtToday(m.kickoff_utc, now)) today.push(m);
      else if (isPtTomorrow(m.kickoff_utc, now)) tomorrow.push(m);
      else later.push(m);
    }
    return { today, tomorrow, later };
  }, [state.matches, now]);

  // The page-level timer is only for the shared opening of the next slate.
  // Once that slate is open, individual match cards own their lock timers.
  const nextMatch = [...state.matches]
    .filter((m) => m.status === "scheduled")
    .sort((a, b) => a.kickoff_utc.localeCompare(b.kickoff_utc))[0];
  const nextWindowOpenMs = nextMatch ? new Date(nextMatch.pick_window_open_utc).getTime() : Infinity;
  const waitingForNextWindow = nextWindowOpenMs > now;
  const scoringMatch = [...state.matches]
    .filter((m) => m.status === "scheduled" && m.home !== "TBD" && m.away !== "TBD")
    .sort((a, b) => a.kickoff_utc.localeCompare(b.kickoff_utc))
    .find((m) => new Date(m.lock_at_utc).getTime() > now) ?? nextMatch;

  return (
    <div className="page">
      <PageHeader
        title="Call the Score"
        tone="peach"
        sub={
          waitingForNextWindow
            ? <>Next window opens in <strong>{countdown(nextWindowOpenMs, now)}</strong></>
            : <>Score + winner · <strong>up to {scoringMatch?.max_points ?? 0} points</strong></>
        }
      />

      {viewerRole === "spectator" && (
        <div className="spectator-banner">
          <span aria-hidden="true">👀</span>
          <div><strong>Spectator mode</strong><small>Live prediction wall · Read-only.</small></div>
        </div>
      )}

      <Section
        title={`Today · ${formatPtDate(now)} (PT)`}
        matches={groups.today}
        state={state}
        myPicks={myPicks}
        refresh={refresh}
        now={now}
      />

      {(groups.tomorrow.length > 0 || groups.later.length > 0) && (
        <div className="upcoming-zone">
          <div className="upcoming-zone__head">
            <FootballIcon className="upcoming-zone__ball" />
            <div>
              <div className="upcoming-zone__title">Next on the fixture wall</div>
              <div className="upcoming-zone__sub">The slate opens together; every match has its own lock timer.</div>
            </div>
          </div>
          <Section title="Tomorrow" matches={groups.tomorrow} state={state} myPicks={myPicks} refresh={refresh} now={now} />
          <Section title="Later" matches={groups.later} state={state} myPicks={myPicks} refresh={refresh} now={now} />
        </div>
      )}
    </div>
  );
}

function Section({
  title, matches, state, myPicks, refresh, now,
}: {
  title: string;
  matches: Match[];
  state: ReturnType<typeof useApp>["state"];
  myPicks: ReturnType<typeof useApp>["myPicks"];
  refresh: () => Promise<void>;
  now: number;
}) {
  if (matches.length === 0) return null;
  return (
    <section className="fixture-section">
      <div className="section-label section-label--fixture">
        <span>{title.toUpperCase()}</span>
        <span>{matches.length} MATCH{matches.length === 1 ? "" : "ES"}</span>
      </div>
      {matches.map((match) => (
        <MatchCard
          key={match.id}
          match={match}
          state={state}
          initial={myPicks[match.id] ?? null}
          refresh={refresh}
          now={now}
        />
      ))}
    </section>
  );
}

function MatchCard({
  match, state, initial, refresh, now,
}: {
  match: Match;
  state: ReturnType<typeof useApp>["state"];
  initial: MyPick | null;
  refresh: () => Promise<void>;
  now: number;
}) {
  const { viewerRole } = useApp();
  const live = match.status === "live";
  // The PT-day slate opens together, then each match closes independently.
  const openMs = new Date(match.pick_window_open_utc).getTime();
  const lockMs = new Date(match.lock_at_utc).getTime();
  const locked = match.locked || now >= lockMs;
  const windowOpen = now >= openMs;
  const teamsKnown = match.home !== "TBD" && match.away !== "TBD";
  const windowAcceptsPicks = !locked && windowOpen && teamsKnown;
  const canPredict = viewerRole === "player" && windowAcceptsPicks;
  const cls = ["card", "match-card"];
  if (live || windowAcceptsPicks) cls.push("card--peach");
  if (!windowAcceptsPicks) cls.push("match-card--upcoming");

  return (
    <div className={cls.join(" ")}>
      <div className="match-card__meta">
        <span className="round-stamp">{match.round}</span>
        <div className={`when ${locked ? "when--locked" : ""}`}>
          {live && <span><span className="live-dot" />LIVE {match.home_score}–{match.away_score}</span>}
          {!live && <>{formatLocalTime(match.kickoff_utc)}{locked && match.status === "scheduled" && " · LOCKED"}</>}
        </div>
      </div>

      <div className="fixture-board">
        <span className="fixture-board__team"><TeamBadge code={match.home} /></span>
        <span className="fixture-board__vs">VS</span>
        <span className="fixture-board__team"><TeamBadge code={match.away} /></span>
      </div>

      {canPredict && (
        <>
          <div className="match-lock-timer"><span aria-hidden="true">⏱</span> Locks in <strong>{countdown(lockMs, now)}</strong></div>
          <PickEditor
            key={`${match.id}-${initial?.submitted_at || "new"}`}
            match={match}
            initial={initial}
            onSubmitted={refresh}
          />
        </>
      )}

      {!locked && !teamsKnown && (
        <div className="opens-later"><span aria-hidden="true">◷</span> Teams to be confirmed</div>
      )}

      {!locked && teamsKnown && !windowOpen && (
        <div className="opens-later">
          <span aria-hidden="true">◷</span> Picks open in <strong>{countdown(openMs, now)}</strong>
        </div>
      )}

      {(locked || viewerRole === "spectator") && match.picks && match.picks.length > 0 && (
        <>
          <div className="section-label picks-label">PREDICTION WALL</div>
          <PredictionPills match={match} players={state.players} />
        </>
      )}
    </div>
  );
}

function PickEditor({
  match, initial, onSubmitted,
}: {
  match: Match;
  initial: MyPick | null;
  onSubmitted: () => Promise<void>;
}) {
  const [home, setHome] = useState(initial?.home_score ?? 0);
  const [away, setAway] = useState(initial?.away_score ?? 0);
  const [winner, setWinner] = useState<"home" | "away" | null>(() =>
    impliedWinner(initial?.home_score ?? 0, initial?.away_score ?? 0) ?? initial?.predicted_winner ?? null
  );
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  // Once a pick exists, lock the controls by default so accidental scroll-taps
  // can't change it. New picks (no `initial`) start unlocked so the first save
  // stays friction-free. Tap the lock icon to toggle.
  const [editing, setEditing] = useState<boolean>(!initial);
  // After a successful save, auto-relock if the user doesn't touch anything
  // for this long. Long enough to react and tweak; short enough that you
  // don't forget the card is open. Increase if friends complain it's tight.
  const AUTO_LOCK_MS = 10_000;
  // Countdown shown in the status line while the auto-lock timer is running.
  const [autoLockIn, setAutoLockIn] = useState<number | null>(null);

  useEffect(() => {
    const unchanged = home === (initial?.home_score ?? 0)
      && away === (initial?.away_score ?? 0)
      && winner === (initial?.predicted_winner ?? null);
    if (!winner || (unchanged && status === "idle")) return;
    setStatus("saving");
    const timer = setTimeout(async () => {
      try {
        await submitPrediction(match.id, home, away, winner);
        setStatus("saved");
        await onSubmitted();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "VAR says try again";
        setStatus("error");
        // The server returns "match is locked" with a 409 when the user has a
        // stale tab and tries to save after the lock. Surface a clear message
        // and pull fresh state so the picker disappears (match.locked = true).
        if (msg.includes("locked")) {
          setErrMsg("Prediction window just closed. Refreshing…");
          await onSubmitted();
        } else {
          setErrMsg(msg);
        }
      }
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home, away, winner]);

  // Auto-relock after a successful save. The countdown ticks every second
  // for status-line feedback; the actual relock happens at the end.
  // Any change to home/away/winner restarts the save cycle, which clears
  // `status` back to "saving" and so cancels this effect (no relock).
  useEffect(() => {
    if (status !== "saved" || !editing) {
      setAutoLockIn(null);
      return;
    }
    let remaining = Math.ceil(AUTO_LOCK_MS / 1000);
    setAutoLockIn(remaining);
    const tick = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(tick);
        setEditing(false);
        setAutoLockIn(null);
      } else {
        setAutoLockIn(remaining);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [status, editing]);

  const scoreWinner = impliedWinner(home, away);
  const changeHome = (next: number) => {
    setHome(next);
    const forced = impliedWinner(next, away);
    if (forced) setWinner(forced);
  };
  const changeAway = (next: number) => {
    setAway(next);
    const forced = impliedWinner(home, next);
    if (forced) setWinner(forced);
  };

  return (
    <div className={`prediction-editor ${editing ? "is-editing" : "is-locked"}`}>
      <button
        type="button"
        className="prediction-editor__lock"
        onClick={() => setEditing((v) => !v)}
        aria-pressed={!editing}
        aria-label={editing ? "Lock this pick to prevent accidental changes" : "Unlock to edit this pick"}
        title={editing ? "Lock this pick" : "Unlock to edit"}
      >
        {editing ? "🔓 Lock pick" : "🔒 Tap to edit"}
      </button>

      <div className="stepper">
        <ScoreColumn label="home" score={home} disabled={!editing} onUp={() => changeHome(Math.min(20, home + 1))} onDown={() => changeHome(Math.max(0, home - 1))} />
        <span className="stepper__dash">–</span>
        <ScoreColumn label="away" score={away} disabled={!editing} onUp={() => changeAway(Math.min(20, away + 1))} onDown={() => changeAway(Math.max(0, away - 1))} />
      </div>

      <fieldset className="winner-picker" disabled={!editing}>
        <legend>{scoreWinner ? `${scoreWinner === "home" ? match.home : match.away} leads — winner follows the score` : "Level score — who goes through?"}</legend>
        <button type="button" className={winner === "home" ? "is-selected" : ""} onClick={() => setWinner("home")} aria-pressed={winner === "home"} disabled={!editing || scoreWinner === "away"}>
          <TeamBadge code={match.home} compact />
        </button>
        <button type="button" className={winner === "away" ? "is-selected" : ""} onClick={() => setWinner("away")} aria-pressed={winner === "away"} disabled={!editing || scoreWinner === "home"}>
          <TeamBadge code={match.away} compact />
        </button>
      </fieldset>

      <div className={`stepper__status stepper__status--${status}`}>
        {!winner && editing && "Choose the winner to save this pick"}
        {winner && status === "idle" && initial && editing && "✓ Your saved pick — tap the lock when you're done"}
        {winner && status === "idle" && initial && !editing && "🔒 Locked — tap the lock above to change"}
        {status === "saved" && autoLockIn !== null && `✓ Pick saved — auto-locks in ${autoLockIn}s`}
        {status === "saved" && autoLockIn === null && "✓ Pick saved. Confidence: enormous."}
        {status === "saving" && "Sending it upfield…"}
        {status === "error" && (errMsg || "VAR says try again")}
      </div>
    </div>
  );
}

function impliedWinner(home: number, away: number): "home" | "away" | null {
  if (home > away) return "home";
  if (away > home) return "away";
  return null;
}

function ScoreColumn({
  label, score, onUp, onDown, disabled = false,
}: {
  label: string; score: number; onUp: () => void; onDown: () => void; disabled?: boolean;
}) {
  return (
    <div className="score-column">
      <button type="button" className="stepper__btn" aria-label={`Increase ${label} score`} onClick={onUp} disabled={disabled}>▲</button>
      <span className="stepper__score" aria-live="polite">{score}</span>
      <button type="button" className="stepper__btn" aria-label={`Decrease ${label} score`} onClick={onDown} disabled={disabled || score === 0}>▼</button>
    </div>
  );
}
